import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSnackbar } from 'notistack';

// MUI
import {
  CancelRounded,
  DownloadRounded,
  UndoRounded,
  Fullscreen,
  PhotoLibraryOutlined,
} from '@mui/icons-material';
import { Box, Button, IconButton, ThemeProvider, Tooltip, Typography } from '@mui/material';

// common
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import NotFoundBox from 'common/components/NotFoundBox';
import useService from 'common/hook/useService';
import useSchoolYearMonthFilter from 'common/hook/useSchoolYearMonthFilter';
import useIsGuest from 'common/hook/useIsGuest';
import useGetRole from 'common/hook/useGetRole';
import { buildResourceSearchStr } from 'common/utils/schoolYearMonths';
import { MUITheme } from 'config/setupTheme.ts';
import { TemmplateSettings } from 'svg';
import exportAsPng from 'libraries/html-to-png';

// screen
import { ClientModel, MMapNutritionMenuModel } from 'modules/map/types';
import {
  DailyDishFilterModel,
  DailyDishStatusEnum,
  defaultSettings,
} from 'modules/map/components/daily-dish/types';
import { formatAbridgedWeekTitle } from 'modules/map/components/daily-dish/formatAbridgedWeekTitle';
import NewTemplate from 'modules/map/components/daily-dish/templates/NewTemplate';
import TemplateSettingsPanel from 'modules/map/components/daily-dish/components/template-settings-panel';
import { settingsReducer } from 'modules/map/components/daily-dish/reducer';
import ExportingImage from 'modules/map/components/daily-dish/components/ExportingImage';
import Filter from 'modules/map/components/daily-dish/components/form/Filter';
import SampleMealPhotosDialog from 'modules/map/components/daily-dish/sample-meal/SampleMealPhotosDialog';
import { sampleMealWeekHasPhotos } from 'modules/map/components/daily-dish/sample-meal/eatingDays';
import { resolveSampleMealWeekStart } from 'modules/map/components/daily-dish/sample-meal/weekStart';
import { KIOS_STORAGE } from 'api/API_App';
import {
  imagesForWindow,
  pickWeeklyLook,
  resolveWindowId,
  cycleWeeklyLook,
} from 'modules/map/components/daily-dish/look/pickWeeklyLook';
import {
  clearLookStorage,
  coerceStoredSettings,
  persistLookRecord,
  readLookStorage,
  stripFavorites,
  writeLookStorage,
} from 'modules/map/components/daily-dish/look/storage';
import {
  deleteLocalBackground,
  getLocalBackground,
  putLocalBackground,
  resizeBackgroundFile,
  validateBackgroundFile,
} from 'modules/map/components/daily-dish/look/localBackgrounds';

interface Props {
  clientData: ClientModel;
  open?: boolean;
  onClose?: () => void;
}

const DailyDishDialog = (props: Props) => {
  const { clientData, open, onClose } = props;
  const service = useService();
  const isGuest = useIsGuest();
  const { abridgedMenu } = useGetRole(clientData);
  const canViewSampleMeals = abridgedMenu && !isGuest;

  // Tháng/tuần do khung thực đơn của năm học quyết định — hook tự chọn khung mới nhất khi tải xong.
  const [filter, setFilter] = useState<DailyDishFilterModel>({
    state: DailyDishStatusEnum.propose,
    week: 1,
  });
  const { monthOptions, weekOptions, framesFailed, schoolYear, abridgedWeekTitles } =
    useSchoolYearMonthFilter(filter, setFilter);

  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [sampleMealOpen, setSampleMealOpen] = useState(false);

  const weekStart = useMemo(
    () =>
      resolveSampleMealWeekStart(
        typeof filter.week === 'number' ? filter.week : undefined,
        weekOptions
      ),
    [filter.week, weekOptions]
  );

  const [templateSettings, dispatchSettings] = useReducer(settingsReducer, defaultSettings);
  const [lockedTheme, setLockedTheme] = useState<string | null>(null);
  const [lookOffset, setLookOffset] = useState<number | null>(null);
  const [localBackgroundUrl, setLocalBackgroundUrl] = useState<string | null>(null);
  const skipPersistRef = useRef(true);
  const { enqueueSnackbar } = useSnackbar();

  const selectedWeek = useMemo(
    () => weekOptions.find((week) => week.id === filter.week),
    [weekOptions, filter.week]
  );
  const dateFrom = selectedWeek?.date_from;
  const dateTo = selectedWeek?.date_to;
  const composedWeekTitle = useMemo(
    () =>
      formatAbridgedWeekTitle({
        mode: abridgedWeekTitles[clientData.operationCode],
        weekNumber: typeof filter.week === 'number' ? filter.week : null,
        monthKey: filter.month,
        schoolYearWeek: selectedWeek?.school_year_week,
      }),
    [
      abridgedWeekTitles,
      clientData.operationCode,
      filter.month,
      filter.week,
      selectedWeek?.school_year_week,
    ]
  );

  const previewImages = useMemo(() => {
    if (!dateFrom || !dateTo) {
      return imagesForWindow('general');
    }
    const windowId = lockedTheme ?? resolveWindowId(dateFrom, dateTo, null);
    return imagesForWindow(windowId);
  }, [dateFrom, dateTo, lockedTheme]);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    const hydrate = async () => {
      skipPersistRef.current = true;
      if (!dateFrom || !dateTo) {
        dispatchSettings({ type: 'HYDRATE', settings: defaultSettings });
        setLockedTheme(null);
        setLookOffset(null);
        setLocalBackgroundUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        return;
      }

      const stored = readLookStorage(dateFrom);
      const lock = stored?.lockedTheme ?? null;
      setLockedTheme(lock);
      setLookOffset(stored?.lookOffset ?? null);
      const seed = pickWeeklyLook({
        dateFrom,
        dateTo,
        lockedTheme: lock,
        lookOffset: stored?.lookOffset ?? 0,
      });
      const restored = stored ? coerceStoredSettings(stored.settings, seed) : null;
      dispatchSettings({ type: 'HYDRATE', settings: restored ?? seed });

      if (stored?.localBackground) {
        try {
          const blob = await getLocalBackground(dateFrom);
          if (cancelled) {
            return;
          }
          if (blob) {
            createdUrl = URL.createObjectURL(blob);
            setLocalBackgroundUrl(createdUrl);
          } else {
            setLocalBackgroundUrl(null);
            dispatchSettings({ type: 'UPDATE_LOCAL_BACKGROUND', value: false });
          }
        } catch {
          if (!cancelled) {
            setLocalBackgroundUrl(null);
          }
        }
      } else {
        setLocalBackgroundUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      }
      skipPersistRef.current = false;
    };

    void hydrate();
    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (skipPersistRef.current || !dateFrom || !dateTo) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      // Phải cùng lookOffset với look đang hiện, nếu không persistLookRecord luôn thấy khác seed
      // và ghi nguyên cục settings cho tuần chỉ mới xoay preset.
      const seed = pickWeeklyLook({ dateFrom, dateTo, lockedTheme, lookOffset: lookOffset ?? 0 });
      const record = persistLookRecord({
        lockedTheme,
        localBackground: templateSettings.general.localBackground,
        current: templateSettings,
        seed,
        lookOffset,
      });
      if (!record) {
        clearLookStorage(dateFrom);
        return;
      }
      writeLookStorage(dateFrom, record);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [templateSettings, lockedTheme, lookOffset, dateFrom, dateTo]);

  const handleLockTheme = useCallback(
    (themeId: string | null) => {
      if (!dateFrom || !dateTo) {
        return;
      }
      skipPersistRef.current = true;
      setLockedTheme(themeId);
      setLookOffset(null);
      const seed = pickWeeklyLook({ dateFrom, dateTo, lockedTheme: themeId });
      dispatchSettings({ type: 'HYDRATE', settings: seed });
      setLocalBackgroundUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      if (themeId) {
        writeLookStorage(dateFrom, {
          lockedTheme: themeId,
          settings: null,
          localBackground: false,
          lookOffset: null,
        });
      } else {
        clearLookStorage(dateFrom);
      }
      skipPersistRef.current = false;
    },
    [dateFrom, dateTo]
  );

  const handleResetWeeklyLook = useCallback(() => {
    if (!dateFrom || !dateTo) {
      return;
    }
    skipPersistRef.current = true;
    setLockedTheme(null);
    setLookOffset(null);
    clearLookStorage(dateFrom);
    void deleteLocalBackground(dateFrom);
    setLocalBackgroundUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    dispatchSettings({
      type: 'HYDRATE',
      settings: pickWeeklyLook({ dateFrom, dateTo, lockedTheme: null }),
    });
    skipPersistRef.current = false;
  }, [dateFrom, dateTo]);

  const handleCyclePreset = useCallback(() => {
    if (!dateFrom || !dateTo) {
      return;
    }
    const next = cycleWeeklyLook({
      dateFrom,
      dateTo,
      lockedTheme,
      lookOffset,
    });
    setLookOffset(next.lookOffset);
    void deleteLocalBackground(dateFrom);
    setLocalBackgroundUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    dispatchSettings({ type: 'HYDRATE', settings: next.settings });
  }, [dateFrom, dateTo, lockedTheme, lookOffset]);

  const handleUploadBackground = useCallback(
    async (file: File) => {
      if (!dateFrom) {
        return;
      }
      const invalid = validateBackgroundFile(file);
      if (invalid) {
        enqueueSnackbar({ message: invalid, variant: 'error' });
        return;
      }
      try {
        const blob = await resizeBackgroundFile(file);
        const dropped = await putLocalBackground(dateFrom, blob);
        dropped.forEach((key) => clearLookStorage(key));
        skipPersistRef.current = true;
        dispatchSettings({ type: 'UPDATE_LOCAL_BACKGROUND', value: true });
        setLocalBackgroundUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        writeLookStorage(dateFrom, {
          lockedTheme,
          settings: stripFavorites({
            ...templateSettings,
            general: { ...templateSettings.general, localBackground: true },
          }),
          localBackground: true,
          lookOffset,
        });
        skipPersistRef.current = false;
      } catch (error) {
        const quota = error instanceof DOMException && error.name === 'QuotaExceededError';
        enqueueSnackbar({
          message: quota ? 'Máy hết chỗ lưu ảnh nền.' : 'Không lưu được ảnh nền.',
          variant: 'error',
        });
      }
    },
    [dateFrom, enqueueSnackbar, lockedTheme, lookOffset, templateSettings]
  );

  const contentToPrint = useRef(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Năm lấy từ tháng đã chọn, không phải dayjs().year(): học kỳ 2 nằm ở năm dương lịch sau.
  // Thiếu tuần thì KHÔNG dựng searchStr: bỏ trống một đoạn sẽ làm `day` trượt vào chỗ của `week`.
  const searchStr = useMemo(
    () =>
      filter.week
        ? buildResourceSearchStr({
            monthKey: filter.month,
            week: filter.week,
            day: 0,
            state: filter.state,
          })
        : null,
    [filter.month, filter.state, filter.week]
  );

  const { data, isFetching } = useQuery<MMapNutritionMenuModel | null>({
    queryKey: [searchStr, 'daily-dish'],
    queryFn: async () => {
      if (!service || !searchStr) return null;
      const json = await service.ClientAbridgedMenuController_list({
        params: {
          orgId: clientData.operationCode,
          mode: 'real',
        },
        queries: {
          limit: 50,
          offset: 0,
          searchStr,
        },
      });

      return (json[0] as unknown as MMapNutritionMenuModel) ?? null;
    },
    enabled: (open ?? false) && !!service && !!searchStr,
    // Tháng chưa chốt khung thì searchStr null, query tắt. keepPreviousData của app sẽ
    // giữ tờ tuần trước (vd. tuần 1 tháng 10) trên màn hình.
    placeholderData: undefined,
  });

  const menuFrom = data?.structuredData?.metadata.fromDate;
  const menuTo = data?.structuredData?.metadata.toDate;
  const hasMenu = !!menuFrom && !!menuTo;

  const { data: sampleMeals, isLoading: sampleMealsLoading } = useQuery({
    queryKey: [clientData.operationCode, weekStart, 'sample-meals'],
    queryFn: async () => {
      if (!service || !weekStart) return null;
      return service.LogiController_listSampleMeals({
        queries: { site_code: clientData.operationCode, week_start: weekStart },
      });
    },
    enabled: (open ?? false) && canViewSampleMeals && !!service && !!weekStart && hasMenu,
  });

  const hasSamplePhotos = useMemo(
    () => sampleMealWeekHasPhotos(sampleMeals?.days, { from: menuFrom, to: menuTo }),
    [menuFrom, menuTo, sampleMeals?.days]
  );
  const canOpenSampleMeals = hasMenu && !sampleMealsLoading && hasSamplePhotos;

  const handleDownloadPng = async () => {
    if (contentToPrint.current) {
      try {
        setIsExporting(true);
        const clientLogoUrl = clientData.logo ? `${KIOS_STORAGE}/${clientData.logo}` : undefined;
        await exportAsPng(contentToPrint.current, clientLogoUrl);
      } finally {
        setIsExporting(false);
      }
    }
  };

  const handleToggleSettings = () => {
    setShowSettings((prev) => !prev);
  };

  const handlePreviewFullScreen = () => {
    if (fullscreenContainerRef.current) {
      if (!document.fullscreenElement) {
        fullscreenContainerRef.current
          .requestFullscreen()
          .then(() => {
            setIsFullscreen(true);
          })
          .catch((err) => {
            console.error('Error attempting to enable fullscreen:', err);
          });
      } else {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        });
      }
    }
  };

  return (
    <MuiDialog
      open={open}
      maxWidth="desktop"
      fullWidth
      titleAlign="center"
      disablePortal
      disableCloseBtn
      PaperProps={{
        sx: {
          margin: 2,
          height: '100%',
          maxWidth: showSettings ? '1800px' : 'lg',
          transition: 'max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          height: '100%',
          // overflow: "hidden",
          overflowX: 'scroll',
        }}
      >
        {/* menu UI */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Typography sx={{ textAlign: 'right', m: 2 }}>
            <IconButton onClick={onClose} size="small" color="primary">
              <CancelRounded fontSize="small" />
            </IconButton>
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <Typography variant="h5" sx={{ textAlign: 'center', color: 'primary.main' }}>
              <FormattedMessage id="map.dailyDish" />
            </Typography>
          </Box>

          {/* Content area */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ px: 2, pt: 1 }}>
              {(canViewSampleMeals || data) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  {canViewSampleMeals && (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<PhotoLibraryOutlined />}
                      disabled={!canOpenSampleMeals}
                      onClick={() => setSampleMealOpen(true)}
                    >
                      <FormattedMessage id="map.sampleMealPhotos" />
                    </Button>
                  )}
                  {data && (
                    <Button
                      sx={{
                        height: '30px',
                        minWidth: 0,
                        backgroundColor: 'transparent !important',
                      }}
                      onClick={handleToggleSettings}
                    >
                      <TemmplateSettings height={'30px'} />
                    </Button>
                  )}
                </Box>
              )}
              <Filter
                filter={filter}
                setFilter={setFilter}
                monthOptions={monthOptions}
                weekOptions={weekOptions}
                framesFailed={framesFailed}
              />
            </Box>
            <Box
              ref={fullscreenContainerRef}
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                overflow: 'auto',
                p: 2,
                position: 'relative',
                backgroundColor: isFullscreen ? '#000' : 'transparent',
                '&:fullscreen': {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 0,
                },
              }}
            >
              {/* Loading Overlay */}
              {isExporting && <ExportingImage />}
              {isFetching ? (
                <MuiLoading />
              ) : data && data?.structuredData?.metadata.isUsed ? (
                <ThemeProvider theme={MUITheme('light')}>
                  <Box
                    sx={{
                      width: isFullscreen ? '100vw' : '1152px',
                      height: isFullscreen ? '100vh' : '648px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      ref={contentToPrint}
                      sx={{
                        width: '1920px',
                        height: '1080px',
                        transform: isFullscreen ? 'scale(1)' : 'scale(0.6)',
                        transformOrigin: 'center center',
                      }}
                    >
                      <NewTemplate
                        data={data}
                        clientData={clientData}
                        settings={templateSettings}
                        localBackgroundUrl={localBackgroundUrl}
                        weekTitle={composedWeekTitle}
                      />
                    </Box>
                  </Box>
                </ThemeProvider>
              ) : (
                <NotFoundBox />
              )}

              {/* fullscreen */}
              {data && data?.structuredData?.metadata.isUsed && (
                <Box
                  sx={{
                    position: 'absolute',
                    right: isFullscreen ? '50px' : '30px',
                    bottom: isFullscreen ? '50px' : 'calc(100% - 660px)',
                    zIndex: 9999,
                  }}
                >
                  <Tooltip
                    title={
                      <FormattedMessage id={isFullscreen ? 'exitFullScreen' : 'openFullScreen'} />
                    }
                  >
                    <IconButton
                      size="large"
                      onClick={handlePreviewFullScreen}
                      disabled={!data}
                      sx={{
                        color: '#F8FAFC',
                        backgroundColor: isFullscreen ? 'rgba(0,0,0,0.5)' : 'transparent',
                        '&:hover': {
                          backgroundColor: isFullscreen ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <Fullscreen sx={{ width: '40px', height: '40px' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                p: 2,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                height: '72px',
              }}
            >
              <Button startIcon={<DownloadRounded />} onClick={handleDownloadPng} disabled={!data}>
                <FormattedMessage id="map.download" />
              </Button>
              <Button variant="outlined" startIcon={<UndoRounded />} onClick={onClose}>
                <FormattedMessage id="back" />
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Settings panel */}
        <TemplateSettingsPanel
          settings={templateSettings}
          dispatch={dispatchSettings}
          show={showSettings}
          lockedTheme={lockedTheme}
          previewImages={previewImages}
          schoolYear={schoolYear}
          onLockTheme={handleLockTheme}
          onResetWeeklyLook={handleResetWeeklyLook}
          onCyclePreset={handleCyclePreset}
          onUploadBackground={handleUploadBackground}
        />
      </Box>

      <SampleMealPhotosDialog
        open={sampleMealOpen}
        onClose={() => setSampleMealOpen(false)}
        siteCode={clientData.operationCode}
        weekStart={weekStart}
        fromDate={menuFrom}
        toDate={menuTo}
      />
    </MuiDialog>
  );
};
export default DailyDishDialog;
